import React, { useState, useEffect } from 'react';
import { useNavigate, BrowserRouter as Router, Routes, Route, Outlet, useLocation } from 'react-router-dom';
import { Provider, defaultTheme } from '@adobe/react-spectrum';

import Header from './components/Header';
import Home from './components/Home';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import Account from './components/Account';
import CheckAuthStatus from './components/auth/CheckAuthStatus';
import ProtectedRoute from './components/auth/ProtectedRoute';
import CheckVerificationStatus from './components/auth/CheckVerificationStatus';
import VerifyEmail from './components/auth/VerifyEmail';
import SelectCountryWrapper from './components/SelectCountryWrapper';
import SelectMoneyType from './components/SelectMoneyType';
import FiatCryptoOnboarding from './components/FiatCryptoOnboarding';
import FiatOnboarding from './components/FiatOnboarding';
import CryptoOnboarding from './components/CryptoOnboarding';
import BitcoinTest from './components/BitcoinTest';
import StripeOnboarding from './components/StripeOnboarding';
import CheckStripeOnboardingStatus from './components/CheckStripeOnboardingStatus';
import CreateQrCode from './components/CreateQrCode';
import EnterDisplayName from './components/EnterDisplayName';
import EnterLinkId from './components/EnterLinkId';
import SendResetPasswordEmail from './components/auth/SendResetPasswordEmail';
import ResetPassword from './components/auth/ResetPassword';
import SetLink from './components/SetLink';
import SetButtonCode from './components/SetButtonCode';
import Error from './components/Error';
import CheckoutWrapper from "./components/CheckoutWrapper";
import CheckoutComplete from "./components/CheckoutComplete";
import CheckOnboardingStage from './components/CheckOnboardingStage';
import PageLayout from './components/PageLayout';
import Settings from './components/settings/Settings';
import Analytics from './components/analytics/Analytics';
import Customize from './components/customization/Customize';
import Privacy from './components/Privacy';
import Terms from './components/Terms';
import Contact from './components/Contact';

import { useIsLoggedIn, useIsVerified, useDisplayName, useLink, useQrCode, useQrCodeLink, useOnboardingStage, useButtonCode, useLinkId } from './state';
import './index.css';

function LayoutRoute() {
  const location = useLocation();
  const hideLogoutRoutes = ['/', '/login', '/register'];
  const profileIconRoutes = ['/settings', '/analytics', '/customize', '/privacy', '/terms', '/contact'];

  const showLogout = !hideLogoutRoutes.includes(location.pathname);

  const showProfileIcon = profileIconRoutes.includes(location.pathname);

  return (
    <PageLayout showLogout={showLogout} showProfileIcon={showProfileIcon}>
      <Outlet />
    </PageLayout>
  );
}

function Content({ email, setEmail }) {
  const isLoggedIn = useIsLoggedIn();
  const isVerified = useIsVerified();
  const displayName = useDisplayName();
  const link = useLink();
  const qrCode = useQrCode();
  const qrCodeLink = useQrCodeLink();
  const onboardingStage = useOnboardingStage();
  const buttonCode = useButtonCode();
  const linkId = useLinkId();
  const navigate = useNavigate();
  const location = useLocation();

  console.log('Content rendering with displayName:', displayName);

  useEffect(() => {
    // Check if we're on the Stripe onboarding callback route
    const isStripeCallbackRoute = location.pathname === '/check-stripe-onboarding-status' &&
      location.search.includes('returnFromStripe=true');

    // Skip redirects for authenticated routes
    const authenticatedRoutes = ['/analytics', '/account', '/settings'];
    const isAuthenticatedRoute = authenticatedRoutes.includes(location.pathname);

    console.log(location.pathname);

    // Skip all redirects if we're on the Stripe callback route or authenticated routes
    if (isStripeCallbackRoute) {
      console.log('On protected route, skipping redirects');
      return;
    }

    if (isVerified === false) {
      console.log('isVerified: ', isVerified);
      console.log('Navigating to verify-email');
      navigate('/verify-email');
      return;
    }

    if (onboardingStage === 'select-country') {
      console.log('Navigating to select country');
      navigate('/onboarding/select-country');
      return;
    }

    if (onboardingStage === 'enter-display-name') {
      console.log('Navigating to enter-display-name');
      navigate('/onboarding/enter-display-name');
      return;
    }

    if (onboardingStage === 'enter-link-id') {
      console.log('Navigating to enter-link-id');
      navigate('/onboarding/enter-link-id');
      return;
    }

    if (onboardingStage === 'select-money-type') {
      console.log('Navigating to select-money-type');
      navigate('/onboarding/select-money-type');
      return;
    }

    if (onboardingStage === 'fiat-crypto-onboarding') {
      console.log('Navigating to fiat-crypto-onboarding');
      navigate('/onboarding/fiat-crypto-onboarding');
      return;
    }

    if (onboardingStage === 'fiat-onboarding') {
      console.log('Navigating to fiat-onboarding');
      navigate('/onboarding/fiat-onboarding');
      return;
    }

    if (onboardingStage === 'crypto-onboarding') {
      console.log('Navigating to crypto-onboarding');
      navigate('/onboarding/crypto-onboarding');
      return;
    }

    if (isAuthenticatedRoute) {
      console.log('On protected route, skipping redirects');
      return;
    }
  }, [isLoggedIn, isVerified, onboardingStage, navigate, location]);

  if (isLoggedIn === null) {
    console.log('Rendering CheckAuthStatus');
    return <CheckAuthStatus />;
  }

  if (isLoggedIn === false) {
    console.log('Rendering Home');
    return <Home email={email} setEmail={setEmail} />;
  }

  if (isVerified === null) {
    console.log('Rendering CheckVerificationStatus');
    return <CheckVerificationStatus />;
  }

  if (onboardingStage === null) {
    console.log('Rendering CheckOnboardingStage');
    return <CheckOnboardingStage />;
  }

  if (onboardingStage !== 'complete') {
    return null;
  }

  if (location.pathname === '/analytics' || location.pathname === '/settings' || location.pathname === '/customize') {
    return null;
  }

  if (!link || !linkId) {
    return <SetLink />;
  }

  if (!qrCode || !(qrCode && qrCodeLink === link)) {
    return <CreateQrCode />;
  }

  if (!buttonCode) {
    return <SetButtonCode />;
  }

  console.log('rendering account.js');

  return <Account email={email} />;
}

const App = () => {
  const [email, setEmail] = useState('');

  return (
    <Provider theme={defaultTheme} colorScheme="light">
      <Router>
        <div >
          <Header />
          <Routes>
            {/* Authentication and error routes */}
            <Route path='/error' element={<Error />} />
            <Route path='/forgot-password' element={<SendResetPasswordEmail />} />
            <Route path='/reset-password' element={<ResetPassword />} />
            <Route path='/return' element={<CheckoutComplete />} />
            {/* Handle Stripe callback route separately, outside of Content component */}
            <Route path='/check-stripe-onboarding-status' element={<CheckStripeOnboardingStatus />} />

            <Route element={<LayoutRoute />}>
              <Route path='/login' element={<Login />} />
              <Route path='/register' element={<Register />} />
              <Route path='/verify-email' element={<VerifyEmail />} />
              <Route path='/privacy' element={<Privacy />} />
              <Route path='/terms' element={<Terms />} />
              <Route path='/contact' element={<Contact />} />
            </Route>

            <Route element={<ProtectedRoute />}>
              <Route element={<LayoutRoute />}>
                <Route path='/analytics' element={<Analytics />} />
                <Route path='/settings' element={<Settings />} />
                <Route path='/customize' element={<Customize />} />
                <Route path='/stripe-onboarding' element={<StripeOnboarding />} />
                <Route path='/onboarding/select-country' element={<SelectCountryWrapper />} />
                <Route path='/onboarding/enter-display-name' element={<EnterDisplayName />} />
                <Route path='/onboarding/enter-link-id' element={<EnterLinkId />} />
                {/* <Route path='/onboarding/select-money-type' element={<SelectMoneyType />} /> */}
                <Route path='/onboarding/fiat-crypto-onboarding' element={<FiatCryptoOnboarding />} />
                <Route path='/onboarding/fiat-onboarding' element={<FiatOnboarding />} />
                <Route path='/onboarding/crypto-onboarding' element={<CryptoOnboarding />} />
              </Route>
            </Route>

            <Route exact path='/' element={<Content email={email} setEmail={setEmail} />} />
            <Route path='/account' element={<Content email={email} setEmail={setEmail} />} />
            {/* Catch-all route at the end */}
            <Route path='*' element={<CheckoutWrapper />} />
          </Routes>
        </div>
      </Router>
    </Provider>
  );
};

export default App;
